import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { WorkFlow, Steps } from 'models/workflow-step';
import AddUpdateStepDialog from './add-update-step.components';
import ConfirmationDialog from 'components/orders/shared/confirmation-dialog.component';

type WorkflowStepsProps = {
    workflow: WorkFlow;
    onStepAdd: (step: Steps) => void;
    onStepUpdate: (step: Steps) => void;
    onStepDelete: (stepId: string) => void;
    onStepMove: (currentIndex: number, newIndex: number) => void;
};

const WorkflowSteps: FC<WorkflowStepsProps> = ({ workflow, onStepAdd, onStepUpdate, onStepDelete, onStepMove }) => {
    const { t: translate } = useTranslation(['lab-settings', 'workflow', 'common', 'milling-jobs']);
    const [open, setOpen] = useState(false);
    const [openConfirmDialog, setConfirmDialog] = useState(false);
    const [stepId, setStepId] = useState('');
    const [step, setStep] = useState<Steps>({} as Steps);

    const openAddStepModal = () => {
        setOpen(true);
    };

    const onDialogClose = () => {
        if (step?._id) {
            setStep({} as Steps);
        }
        setOpen(false);
    };

    const onAdd = (step: Steps) => {
        setOpen(false);
        onStepAdd(step);
    };

    const onUpdate = (step: Steps) => {
        setStep({} as Steps);
        setOpen(false);
        onStepUpdate(step);
    };

    const handleDeleteClick = (deleteStepId: string) => {
        setStepId(deleteStepId);
        setConfirmDialog(true);
    };

    const handleClose = () => {
        setConfirmDialog(false);
    };

    const handleOk = async () => {
        onStepDelete(stepId);
        setConfirmDialog(false);
    };

    const openUpdateModalWithStep = (selectedStep: Steps) => {
        setStep(selectedStep);
        setOpen(true);
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || result.source.index === result.destination.index) return;
        onStepMove(result.source.index, result.destination.index);
    };

    return (
        <Fragment>
            <Box mb={1}>
                <Button onClick={openAddStepModal} startIcon={<AddIcon />}>
                    {translate('ADD_STEP')}
                </Button>
            </Box>

            <Paper variant="outlined">
                <DragDropContext onDragEnd={handleDragEnd}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width={40} />
                                    <TableCell>{translate('lab-settings:WORKFLOW_STEPS')}</TableCell>
                                    <TableCell>{translate('workflow:MAX_HOUR_IN_STEP')}</TableCell>
                                    <TableCell>{translate('milling-jobs:MILLING_DEADLINE')}</TableCell>
                                    <TableCell>{translate('common:EXTERNAL_TECHNICIANS')}</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <Droppable droppableId="workflow-steps">
                                {(provided) => (
                                    <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                                        {workflow?.steps?.map((workflowStep, index) => (
                                            <Draggable
                                                key={workflowStep._id}
                                                draggableId={workflowStep._id || `step-${index}`}
                                                index={index}
                                            >
                                                {(dragProvided) => (
                                                    <TableRow
                                                        ref={dragProvided.innerRef}
                                                        {...dragProvided.draggableProps}
                                                        hover
                                                        onClick={() => openUpdateModalWithStep(workflowStep)}
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        <TableCell
                                                            {...dragProvided.dragHandleProps}
                                                            onClick={(e) => e.stopPropagation()}
                                                            sx={{ cursor: 'grab', width: 40 }}
                                                        >
                                                            <DragIndicatorIcon color="action" />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">{workflowStep.name}</Typography>
                                                            {workflowStep.initiatesSubcontract ? (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="primary"
                                                                    display="block"
                                                                >
                                                                    {translate('workflow:INITIATES_SUBCONTRACT')}
                                                                </Typography>
                                                            ) : undefined}
                                                            {workflowStep.description && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {workflowStep.description}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>{workflowStep.maxHourInStep || '—'}</TableCell>
                                                        <TableCell>
                                                            {workflowStep.useMillingDeadline
                                                                ? `${translate('common:YES')}, ${workflowStep.millingDeadlineDays} ${translate('lab-settings:DAYS')}`
                                                                : translate('common:NO')}
                                                        </TableCell>
                                                        <TableCell>
                                                            {workflowStep.defaultExtUsers
                                                                ?.map((user) => user.fullName)
                                                                .join(', ')}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <IconButton
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteClick(workflowStep._id as string);
                                                                }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </TableBody>
                                )}
                            </Droppable>
                        </Table>
                    </TableContainer>
                </DragDropContext>
            </Paper>

            <AddUpdateStepDialog
                step={step}
                open={open}
                onDialogClose={onDialogClose}
                onAdd={onAdd}
                onUpdate={onUpdate}
                editInfoText={translate('workflow:EDIT_DELETE_INFO_TEXT')}
            />
            <ConfirmationDialog
                open={openConfirmDialog}
                handleClose={handleClose}
                handleOk={handleOk}
                title={translate('workflow:CONFIRM_TITLE')}
                confirm={translate('workflow:CONFIRM_DELETE')}
                process={translate('common:DELETE')}
                deleteInfoText={translate('workflow:EDIT_DELETE_INFO_TEXT')}
            />
        </Fragment>
    );
};

export default WorkflowSteps;
